import { useEffect, useState } from 'react';
import { Check, X, ArrowLeftRight, Loader2, Clock } from 'lucide-react';

import { fetchLatestMessageWithMetadata, insertChatMessage } from '@/queries/chat';
import { fetchOfferInterestSummary, respondToOfferInterest } from '@/queries/offers';
import { toast } from '@/lib/toast';
import { cn } from '@/lib/utils';
import { ItemPin } from './ItemPin';
import { formatUsdCents } from '@/lib/formatMoney';

interface InlineOfferCardProps {
	mine: boolean;
	conversationId: string;
	messageKind: 'offer' | 'counter_offer' | 'offer_accepted' | 'offer_declined' | 'offer_withdrawn';
	metadata: any;
	currentUserId: string | undefined;
	onCounter: (interestId: string, prevAmountCents: number) => void;
}

/**
 * Renders an inline FB-Messenger-style offer card. The latest offer/counter
 * for the underlying interest is actionable; older ones are read-only
 * "snapshots" showing the state at the time.
 */
export const InlineOfferCard = ({
	mine,
	conversationId,
	messageKind,
	metadata,
	currentUserId,
	onCounter,
}: InlineOfferCardProps) => {
	const interestId = metadata?.interest_id as string | undefined;
	const itemId = metadata?.item_id as string | undefined;
	const amountCents = (metadata?.amount_cents as number | undefined) ?? null;
	const [status, setStatus] = useState<string | null>(null);
	const [latestCents, setLatestCents] = useState<number | null>(amountCents);
	const [isLatestForInterest, setIsLatestForInterest] = useState(false);
	const [busy, setBusy] = useState(false);

	// Fetch the live interest + check whether THIS message is the latest event
	// for that interest in this thread (only the latest can be acted on).
	useEffect(() => {
		if (!interestId) return;
		let cancelled = false;
		(async () => {
			const [interest, latest] = await Promise.all([
				fetchOfferInterestSummary(interestId),
				fetchLatestMessageWithMetadata(conversationId, { interest_id: interestId }),
			]);
			if (cancelled) return;
			setStatus(interest?.status ?? null);
			setLatestCents(
				(interest?.counter_offer_cents as number | null) ??
					(interest?.offer_cents as number | null) ??
					amountCents
			);
			setIsLatestForInterest(
				!!latest &&
					!!metadata &&
					(latest.metadata as any)?.message_uid === metadata?.message_uid
			);
		})();
		return () => {
			cancelled = true;
		};
	}, [interestId, conversationId, amountCents, metadata]);

	const price =
		typeof latestCents === 'number'
			? `${formatUsdCents(latestCents)}`
			: typeof amountCents === 'number'
			? `${formatUsdCents(amountCents)}`
			: '—';

	const headline =
		messageKind === 'offer'
			? mine
				? 'You sent an offer'
				: 'New offer'
			: messageKind === 'counter_offer'
			? mine
				? 'You countered'
				: 'Counter-offer'
			: messageKind === 'offer_accepted'
			? 'Offer accepted'
			: messageKind === 'offer_declined'
			? 'Offer declined'
			: 'Offer withdrawn';

	const statusBadge =
		status === 'accepted' ? (
			<span className="inline-flex items-center gap-1 text-[10.5px] font-medium text-success bg-success/15 rounded-full px-2 py-0.5">
				<Check className="h-3 w-3" />
				Accepted
			</span>
		) : status === 'declined' ? (
			<span className="inline-flex items-center gap-1 text-[10.5px] font-medium text-destructive bg-destructive/15 rounded-full px-2 py-0.5">
				<X className="h-3 w-3" />
				Declined
			</span>
		) : status === 'countered' ? (
			<span className="inline-flex items-center gap-1 text-[10.5px] font-medium text-info bg-info/15 rounded-full px-2 py-0.5">
				<ArrowLeftRight className="h-3 w-3" />
				Countered
			</span>
		) : status === 'pending' ? (
			<span className="inline-flex items-center gap-1 text-[10.5px] font-medium text-[hsl(var(--active-gold))] bg-[hsl(var(--active-gold)/0.12)] rounded-full px-2 py-0.5">
				<Clock className="h-3 w-3" />
				Pending
			</span>
		) : null;

	const canAct =
		!!currentUserId &&
		!busy &&
		isLatestForInterest &&
		((messageKind === 'offer' && !mine && status === 'pending') ||
			(messageKind === 'counter_offer' && !mine && status === 'countered'));

	const handleRespond = async (next: 'accepted' | 'declined') => {
		if (!interestId) return;
		setBusy(true);
		try {
			// If responding to a counter, the seller's counter becomes the buyer's accepted amount.
			const offerOverride =
				messageKind === 'counter_offer' && next === 'accepted' && typeof latestCents === 'number'
					? latestCents
					: undefined;
			const { error } = await respondToOfferInterest(interestId, {
				status: next,
				...(offerOverride !== undefined ? { offer_cents: offerOverride } : {}),
			});
			if (error) throw error;

			// Post a card reflecting the new state
			await insertChatMessage({
				conversationId,
				senderId: currentUserId!,
				kind: next === 'accepted' ? 'offer_accepted' : 'offer_declined',
				body: next === 'accepted' ? 'Offer accepted' : 'Offer declined',
				metadata: { interest_id: interestId, item_id: itemId, amount_cents: latestCents },
			});
			setStatus(next);
			toast.success(next === 'accepted' ? 'Offer accepted' : 'Offer declined');
		} catch (err: any) {
			toast.error('Could not update offer', { description: err?.message });
		} finally {
			setBusy(false);
		}
	};

	const handleCounter = () => {
		if (!interestId || typeof latestCents !== 'number') return;
		onCounter(interestId, latestCents);
	};

	return (
		<div
			className={cn(
				'max-w-[82%] rounded-2xl px-3.5 py-3 space-y-2.5',
				mine
					? 'bg-primary/12 border border-primary/30 rounded-br-md'
					: 'bg-white/[0.05] border border-white/[0.06] rounded-bl-md'
			)}
		>
			<div className="flex items-center justify-between gap-2">
				<div className="text-[11.5px] text-muted-foreground font-medium uppercase tracking-wide">
					{headline}
				</div>
				{statusBadge}
			</div>
			<div className="flex items-baseline gap-2">
				<span className="text-[20px] font-semibold tabular-nums text-[hsl(var(--active-gold))] leading-none">
					{price}
				</span>
			</div>
			{itemId && <ItemPin itemId={itemId} title={metadata?.item_title} variant="pin" />}
			{canAct && (
				<div className="flex flex-wrap gap-1.5 pt-1">
					<button
						type="button"
						onClick={() => handleRespond('accepted')}
						disabled={busy}
						className="h-8 px-3 rounded-full text-[12px] font-medium bg-[hsl(var(--active-gold))] text-black hover:opacity-90 disabled:opacity-50 inline-flex items-center gap-1"
					>
						{busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
						Accept
					</button>
					<button
						type="button"
						onClick={handleCounter}
						disabled={busy}
						className="h-8 px-3 rounded-full text-[12px] font-medium bg-white/[0.06] border border-white/[0.08] text-foreground hover:bg-white/[0.1] disabled:opacity-50 inline-flex items-center gap-1"
					>
						<ArrowLeftRight className="h-3 w-3" />
						Counter
					</button>
					<button
						type="button"
						onClick={() => handleRespond('declined')}
						disabled={busy}
						className="h-8 px-3 rounded-full text-[12px] font-medium bg-transparent text-muted-foreground hover:text-foreground hover:bg-white/[0.04] disabled:opacity-50 inline-flex items-center gap-1"
					>
						<X className="h-3 w-3" />
						Decline
					</button>
				</div>
			)}
		</div>
	);
};

export default InlineOfferCard;
